/* eslint-disable import/first */

import i18n, { lngs } from '../i18n';

export {
  createReducer,
  createRequestReducer,
  defaultListInitialState,
  defaultGetReducer,
  defaultObjectInitialState,
  defaultObjectReducer,
  createSingleItemReducer
} from './redux';

export {
  findCityById
} from './cities';

export {
  buildSearchParams,
  buildApiParams,
  getEarliestBookingDate,
  getEarliestDropDate,
  getDefaultBookingDate,
  getDefaultDropDate,
  extractSearchParams,
  updateBookingBeginDate,
  updateBookingEndDate,
  updateBookingBeginDay,
  updateBookingEndDay
} from './search';

export {
  getPriceInCurrency,
  formatPrice
} from './price';

export function scrollToTop(container, speed, top) {
  container = (container && !container.scrollTo) ? container : false;
  speed = (speed) ? speed : 300;
  top = top || 0;

  const start = (container) ? container.scrollTop : window.pageYOffset;
  const distance = start - top;
  const easing = t => (t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1);

  let animationInterval;
  let time = 0;
  let part = 0;
  let position = start + top;

  if (animationInterval) {
    clearInterval(animationInterval);
  }

  animationInterval = setInterval(() => {
    time += 16;
    part = time / speed;
    part = (part > 1) ? 1 : part;
    position = start - (distance * easing(part));

    if (container) {
      container.scrollTop = Math.floor(position);
    } else {
      window.scrollTo(0, Math.floor(position));
    }

    if ((start >= top && position <= top) || (start < top && position >= top)) {
      if (container) {
        container.scrollTop = top;
      } else {
        window.scrollTo(0, top);
      }

      clearInterval(animationInterval);
    }
  }, 16);
}


export function detectBootstrapScreen() {
  if (!process.env.REACT_APP_IS_CLIENT) {
    return null;
  }

  const {
    innerWidth
  } = window;

  if (innerWidth > 1199) {
    return 'lg';
  }

  if (innerWidth > 991 && innerWidth < 1200) {
    return 'md';
  }

  if (innerWidth > 767 && innerWidth < 992) {
    return 'sm';
  }

  if (innerWidth < 768) {
    return 'xs';
  }
}

/**
 * Checks if value is in store and dispatches loading action if value is falsy.
 * @param {Object} store. Storage instance
 * @param {Boolean} shouldWait. Return awaiting promise created by action creator.
 * @param {Function} checkSelector. Check if value is in store.
 * @param {Function} actionCreator. Creates loading action.
 * @param {Array} actionCreatorParams. Additional params for action creator.
 */
export function createPromise(
  { dispatch, getState },
  shouldWait,
  checkSelector,
  actionCreator,
  ...actionCreatorParams
) {
  const state = getState();
  const checkTarget = checkSelector(state);
  if (!checkTarget) {
    const promise = dispatch(actionCreator(...actionCreatorParams));
    return shouldWait ? promise : null;
  }
}

/**
 * Creates a function that performs check and load value if not in store
 * @param {Function} checkSelector. Check value is in store.
 * @param {Function} actionCreator. Create action to load value.
 * @param {Array} actionCreatorParams. Additional params to load value.
 */
export function createDefaultPromise(checkSelector, actionCreator, ...actionCreatorParams) {
  return ({ store, helpers: { shouldWait } }) =>
    createPromise(store, shouldWait, checkSelector, actionCreator, ...actionCreatorParams);
}

export function buildUrl(protocol, domain, lang, path, search) {
  let url = `${protocol}://${domain}`;

  if (lang) {
    url += `/${lang}`;
  }

  if (path) {
    url += `${path}`;
  } else {
    url += '/';
  }

  if (search) {
    url += search;
  }

  return url;
}

export function buildHead(t, language, lngs, config, protocol, domain, location) {
  const head = { meta: [], link: [] };

  if (location) {
    const paths = location.pathname.split('/');
    const pathname = (paths[1] && lngs.indexOf(paths[1]) > -1) ? '/' + paths.slice(2).join('/') : location.pathname;

    head.link.push({
      rel: 'canonical',
      href: buildUrl(protocol, domain, language, pathname)
    });

    lngs.forEach(hreflang => {
      const href = buildUrl(protocol, domain, hreflang, pathname);

      head.link.push({
        rel: 'alternate',
        hreflang,
        href
      });
    });

    head.link.push({
      rel: 'alternate',
      hreflang: 'x-default',
      href: buildUrl(protocol, domain, 'en', pathname)
    });
  }

  if (config) {
    if (config.title) {
      head.title = t('title', { ns: 'meta' });
    }

    if (config.metaDescriptionEn) {
      head.meta.push({ name: 'description', content: t('description', { ns: 'meta' }) });
    }

    if (config.metaKeywordsEn) {
      head.meta.push({ name: 'keywords', content: t('keywords', { ns: 'meta' }) });
    }

    if (config.metaB_verify) {
      head.meta.push({ name: 'B-verify', content: config.metaB_verify });
    }
  }

  return head;
}

export function debouncer(timer, callback, delay) {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }

  return setTimeout(() => {
    callback();
  }, (typeof delay === 'number' ? delay : 350));
}

export function generatePreview(imagePath, width = 0, height = 227) {
  if (!imagePath || imagePath.startsWith('data:')) {
    return imagePath;
  }

  const proxyParams = `/image/unsafe/resize:fill:${width}:${height}:0/plain/`;

  return `${proxyParams}${encodeURIComponent(imagePath)}@jpg`;
}

export function capitalize(str) {
  return str[0].toUpperCase() + str.slice(1);
}

export function getKey(data, key) {
  const lang = capitalize(i18n.language);
  return data && (data[`${key}${lang}`] || data[`${key}En`] || data[key]);
}

export function isPromise(p) {
  return p && Object.prototype.toString.call(p) === '[object Promise]';
}

export const defaultTransform = (ns, field) => (data) => {
  const resources = lngs.reduce((acc, lang) => ({
    ...acc,
    [lang]: { [ns]: {} }
  }), {});

  data.forEach((item) => {
    const defaultKey = item[`${field}En`];

    lngs.forEach((lang) => {
      const key = `${field}${capitalize(lang)}`;

      if (item[key]) {
        resources[lang][ns][defaultKey] = item[key];
      }
    });
  });

  return resources;
};



// WEBPACK FOOTER //
// ./src/utils/index.js